/* eslint-disable react/no-unknown-property */
import classNames from 'classnames';
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';

import { sanitizeAccountNumber, formatAccountNumber } from '@eika-infrastruktur/format';
import { FocusContainer } from '@eika/focus-container';
import { Icon, IconTypes } from '@eika/icon';
import { AttentionLabel, WarningLabel } from '@eika/label';
import { Loader } from '@eika/loader';

import type { AccountNumber, Receiver } from '../../../../generated-types';
import useDebounce from '../../../../hooks/useDebounce';
import { useFuzzy } from '../../../../hooks/useFuzzy';
import sortByKey from '../../../../utils/mapper/sortByKey';
import { SelectedReceiver } from '../../../transaction/SelectedReceiver';
import useSelect from '../../utils/useSelect';

import './ReceiverComboBox.scss';

type ReceiverComboBoxProps = {
    className?: string;
    id: string;
    label: string;
    placeholder: string;
    searchString: string;
    setSearchString: (value: string) => void;
    icon: IconTypes;
    items: Receiver[];
    onSelect: (item?: SelectedReceiver) => void;
    selected?: SelectedReceiver;
    locked: boolean;
    isLoading?: boolean;
    onShowAddReceiver: () => void;
    addReceiverText: string;
    error?: { className: string; message: string };
    setRef?: (el: HTMLInputElement | null) => void;
    isPaymentModalOpen: boolean;
    tabIndex?: number;
    setPlaceholderHeight?: (height: number) => void;
    warning?: { className: string; message: string };
};

const ReceiverComboBox: React.FC<ReceiverComboBoxProps> = ({
    className,
    id,
    label,
    placeholder,
    icon,
    items,
    onSelect,
    searchString,
    setSearchString,
    selected,
    locked,
    isLoading = false,
    onShowAddReceiver,
    addReceiverText,
    error,
    setRef,
    isPaymentModalOpen,
    tabIndex,
    setPlaceholderHeight,
    warning,
}) => {
    const comboBoxRef = useRef<HTMLLabelElement | null>(null);
    const comboBoxListRef = useRef<HTMLUListElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [hasFocus, setHasFocus] = useState(false);

    const fuzzy = useFuzzy<Receiver>(items, { keys: ['accountNumber.value', 'name'] });

    useEffect(() => {
        if (setRef) {
            setRef(inputRef.current);
        }
    }, [inputRef, setRef]);

    const {
        onKeyDownSearchInput,
        onKeyDownSelectItem,
        show: open,
        setShow: setOpen,
    } = useSelect(comboBoxListRef, comboBoxRef as React.RefObject<HTMLDivElement>, inputRef, true);

    const [filteredAccounts, setFilteredAccounts] = useState<Receiver[]>(items);

    const placeholderHeight = useMemo(() => Math.min(150, (Math.max(items.length, 1) * 100) / 1.5), [items]);

    useEffect(() => {
        if (setPlaceholderHeight) {
            setPlaceholderHeight(open ? placeholderHeight : 0);
        }
    }, [open, setPlaceholderHeight, placeholderHeight]);

    useEffect(() => {
        if (!isPaymentModalOpen) {
            setOpen(false);
        }
    }, [setOpen, isPaymentModalOpen]);

    const resetSearch = useCallback(() => {
        setFilteredAccounts(items);
        setHasFocus(true);
        setOpen(true);
    }, [items, setFilteredAccounts, setHasFocus, setOpen]);

    const selectItem = useCallback(
        (item?: Receiver, setFocus = true) => {
            if (setFocus) {
                inputRef.current?.focus();
            }
            setOpen(false);

            if (!item) {
                const accountNumber = { value: sanitizeAccountNumber(searchString) };
                onSelect({ accountNumber });
                setSearchString(formatAccountNumber(accountNumber));
                return;
            }
            onSelect(item);
            setSearchString(item.name);
        },
        [setSearchString, onSelect, setOpen, searchString],
    );

    const isSelected = useCallback(
        (accountNumber: AccountNumber): boolean => {
            if (selected?.accountNumber) {
                return sanitizeAccountNumber(selected.accountNumber.value) === accountNumber.value;
            }
            return false;
        },
        [selected],
    );

    const getItemsFromSearchString = useCallback(
        (searchString: string): Receiver[] => {
            const searchStringWithoutSeparators = sanitizeAccountNumber(searchString);
            const targetSearchWord =
                searchStringWithoutSeparators.length === 0 ? searchString : searchStringWithoutSeparators;
            return fuzzy.search(targetSearchWord).sort(sortByKey('name'));
        },
        [fuzzy],
    );

    const debouncedSearch = useDebounce((value: string) => setFilteredAccounts(getItemsFromSearchString(value)), 200);

    const search = useCallback(
        (value: string) => {
            onSelect(undefined);
            setSearchString(value);
            debouncedSearch(value);
            setOpen(true);
        },
        [setSearchString, onSelect, setOpen, debouncedSearch],
    );

    const listItemKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>, receiver?: Receiver) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                selectItem(receiver);
            }

            if ((e.key === 'Tab' || e.key === 'Esc' || e.key === 'Escape') && receiver) {
                if (hasFocus && open) {
                    e.stopPropagation();
                }
                selectItem(receiver);

                return;
            }

            onKeyDownSelectItem(e);
        },
        [selectItem, hasFocus, open, onKeyDownSelectItem],
    );

    const selectItemOnFocusChange = (value: string) => {
        if (value.length === 0) {
            return;
        }
        if (value === selected?.accountName) {
            return;
        }

        const searchStringWithoutSeparators = sanitizeAccountNumber(value);
        const searchForAccount = items.find((account) => {
            return (
                account.accountNumber.value.includes(
                    searchStringWithoutSeparators.length === 0 ? value : searchStringWithoutSeparators,
                ) || account.name.toLowerCase().includes(value.toLowerCase())
            );
        });
        if (searchForAccount) {
            selectItem(searchForAccount, false);
        } else {
            selectItem(undefined, false);
        }
    };

    const inputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (hasFocus && open) {
                e.stopPropagation();
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                selectItem(filteredAccounts[0]);
            }
            onKeyDownSearchInput(e);
        },
        [selectItem, hasFocus, open, onKeyDownSearchInput, filteredAccounts],
    );

    const formattedAccountNumber = useMemo(
        () => formatAccountNumber(selected?.accountNumber),
        [selected?.accountNumber],
    );
    const searchStringAsAccountNumber = formatAccountNumber({ value: sanitizeAccountNumber(searchString) });

    const inputPlaceholder = useMemo(() => {
        if (selected?.accountName) return selected.accountName;
        if (selected?.accountNumber?.value) return formattedAccountNumber;
        if (selected?.iban?.value) return selected.iban.value;
        return placeholder;
    }, [selected, formattedAccountNumber, placeholder]);

    return (
        <>
            <label
                className={classNames('receiver-combo-box', { active: open && hasFocus, locked }, className)}
                ref={comboBoxRef}
            >
                <FocusContainer
                    containerClassName="receiver-combo-box__focus-container"
                    entry={open}
                    onExit={() => setOpen(false)}
                >
                    <span className="receiver-combo-box__label-text" id={`combo-label-${id}`}>
                        {label}
                    </span>
                    <input
                        className={classNames('receiver-combo-box__input', {
                            selected:
                                (selected?.accountName || (selected?.accountNumber && !selected.accountName)) &&
                                searchString.length === 0,
                        })}
                        aria-controls={`combo-listbox-${id}`}
                        aria-expanded="false"
                        id="combo-input"
                        data-testid="to-account-select__desktop"
                        role="combobox"
                        type="text"
                        value={searchString}
                        placeholder={inputPlaceholder}
                        onFocus={resetSearch}
                        ref={inputRef}
                        onKeyDown={inputKeyDown}
                        onEmptied={() => search('')}
                        onInput={(e) => search(e.currentTarget.value)}
                        autoComplete="off"
                        onBlur={(e) =>
                            e.relatedTarget && comboBoxRef.current?.contains(e.relatedTarget as HTMLElement)
                                ? null
                                : selectItemOnFocusChange(e.currentTarget.value ?? '')
                        }
                        disabled={locked}
                        tabIndex={tabIndex}
                    />
                    <span className={classNames('receiver-combo-box__icon', { 'is-loading': isLoading })}>
                        {isLoading ? (
                            <Loader className="combobox__loader" mini />
                        ) : (
                            <Icon type={locked ? IconTypes.PIN_KODE_24 : icon} />
                        )}
                    </span>

                    <span
                        className={classNames('receiver-combo-box__subtext', {
                            empty:
                                searchStringAsAccountNumber === formattedAccountNumber ||
                                formattedAccountNumber.length === 0,
                        })}
                    >
                        {selected &&
                            searchStringAsAccountNumber !== formattedAccountNumber &&
                            inputPlaceholder !== formattedAccountNumber &&
                            formattedAccountNumber}
                    </span>
                    <ul
                        className={classNames('receiver-combo-box__list', { active: open && hasFocus })}
                        aria-labelledby={`combo-label-${id}`}
                        id={`combo-listbox-${id}`}
                        role="listbox"
                        ref={comboBoxListRef}
                    >
                        {!isLoading && (
                            <>
                                {filteredAccounts.map((item) => {
                                    const selected = isSelected(item.accountNumber);
                                    return (
                                        <li
                                            role="option"
                                            key={`${item.accountNumber.value}-${item.name}`}
                                            className={classNames('receiver-combo-box__list-item', { selected })}
                                            aria-selected={isSelected(item.accountNumber)}
                                        >
                                            <button
                                                type="button"
                                                className="receiver-combo-box__list-item-button"
                                                // https://stackoverflow.com/questions/17769005/onclick-and-onblur-ordering-issue
                                                // ↓ Solves problem with onClick not being called on Safari
                                                onMouseDown={(event) => {
                                                    event.preventDefault();
                                                }}
                                                onClick={() => selectItem(item)}
                                                onKeyDown={(e) => listItemKeyDown(e, item)}
                                            >
                                                <span className="combobox__list-item-left-text">{item.name}</span>
                                                <span className="receiver-combo-box__list-item-left-sub-text">
                                                    {formatAccountNumber(item.accountNumber)}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                                <li className="receiver-combo-box__list-item" role="option" aria-selected={false}>
                                    <button
                                        type="button"
                                        className="receiver-combo-box__list-item-button with-icon"
                                        onClick={onShowAddReceiver}
                                        onKeyDown={listItemKeyDown}
                                    >
                                        <span className="combobox__list-item-left-text">
                                            <Icon type={IconTypes.PLUSS_24} />
                                            {addReceiverText}
                                        </span>
                                    </button>
                                </li>
                            </>
                        )}
                    </ul>
                </FocusContainer>
            </label>
            {error && <WarningLabel className={error.className} text={error.message} />}
            {warning && !error && <AttentionLabel className={warning.className} text={warning.message} />}
        </>
    );
};

export default ReceiverComboBox;
